import type { Control } from "react-hook-form";
import { t } from "ttag";

import { MAX_HEIGHT_FEET, MAX_HEIGHT_INCHES } from "@/shared/models";
import type { BmiForm } from "@/shared/models";
import {
  AnimatedFields,
  Form,
  Input,
  RulerIcon,
  Select,
  WeigherIcon,
} from "@/shared/ui";

const FEET_OPTIONS = Array.from({ length: MAX_HEIGHT_FEET - 3 }, (_, i) => ({
  // starts at 4ft (MAX_HEIGHT_FEET - 3 = number of options from 4 to MAX)
  value: i + 4,
  label: `${i + 4} ft`,
}));

const INCHES_OPTIONS = Array.from(
  { length: MAX_HEIGHT_INCHES + 1 },
  (_, i) => ({
    value: i,
    label: `${i} in`,
  }),
);

interface ImperialFieldsProps {
  control: Control<BmiForm>;
}

export const ImperialFields = ({ control }: ImperialFieldsProps) => {
  return (
    <AnimatedFields className="sm:grid-cols-2">
      <Form.Field
        control={control}
        name="heightFeet"
        render={({ field: { value, ...field } }) => (
          <Form.Item className="mf-masked mf-listen">
            <Form.Label aria-required>{t`Height (feet)`}</Form.Label>
            <Form.Control>
              <Select
                value={value ?? ""}
                options={FEET_OPTIONS}
                placeholder={t`Select feet`}
                leftIcon={<RulerIcon />}
                contentClassName="mf-masked mf-listen"
                {...field}
              />
            </Form.Control>
            <Form.Message compact={false} />
          </Form.Item>
        )}
      />

      <Form.Field
        control={control}
        name="heightInches"
        render={({ field: { value, ...field } }) => (
          <Form.Item className="mf-masked mf-listen">
            <Form.Label aria-required>{t`Height (inches)`}</Form.Label>
            <Form.Control>
              <Select
                value={value ?? ""}
                options={INCHES_OPTIONS}
                placeholder={t`Select inches`}
                leftIcon={<RulerIcon />}
                contentClassName="mf-masked mf-listen"
                {...field}
              />
            </Form.Control>
            <Form.Message compact={false} />
          </Form.Item>
        )}
      />

      <Form.Field
        control={control}
        name="weightInPounds"
        render={() => (
          <Form.Item className="mf-masked mf-listen col-span-full">
            <Form.Label aria-required>{t`Weight (pounds)`}</Form.Label>
            <Form.Control>
              <Input.Root
                type="number"
                placeholder={t`E.g. 286`}
                {...control.register("weightInPounds", { valueAsNumber: true })}
              >
                <Input.Slot>
                  <WeigherIcon />
                </Input.Slot>
              </Input.Root>
            </Form.Control>
            <Form.Message compact={false} />
          </Form.Item>
        )}
      />
    </AnimatedFields>
  );
};
